// The show's day sheet, /deals/shows/:id (module 10 U149, U159 to U161, U163; the brief
// DEALS-PAGE-BRIEF-2026-09-22.md section 8; drawn in screens/2026-09-22-deals-concepts/show-a-daysheet.html).
//
// Three real tabs: Agenda, Not booked yet, After the show. U152 is the rule every line is written to:
// each thing drawn comes from the show read, and a thing with no source yet is NOT drawn -- so the mock's
// account owner, attending and action columns on "Not booked yet", and "what came of it" and the
// follow-up on "After the show", are absent until something reads them. (The follow-up is read since B1674,
// and the debriefs since B1703; "what came of it" is still not drawn.) The tracker has no column for
// an owner or for attending, and no lane reads the mail after a meeting yet; a column saying "Not known"
// on every row would be a claim that someone looked.
import { ActionButton } from '../parts/index.js';
import { useEffect, useMemo, useState, type KeyboardEvent } from 'react';
import { dayMonthTime } from '../day.js';
import { Loading, useLoad, type PageProps } from '../records/shared.js';
import { showApi, type ShowMeeting, type ShowSheet, type ShowTarget } from './api.js';
import { DebriefList, DebriefRecorder } from './Debrief.js';
import { AddNotes } from '../notes/AddNotes.js';
import { byDay, cityOfZone, clashWords, followUpWords, clockOf, companyOf, dayHeading, interestedOf, oursOf, plateOf, readFailedWords, showDates } from './words.js';
import './show.css';

type Tab = 'agenda' | 'targets' | 'after';
type Filter = 'everyone' | 'mine' | 'clashes';

const TABS: Array<{ key: Tab; label: string }> = [
  { key: 'agenda', label: 'Agenda' }, { key: 'targets', label: 'Not booked yet' }, { key: 'after', label: 'After the show' },
];

export function ShowPage({ store, open, id }: PageProps & { id: string }) {
  const [loaded, reload] = useLoad(() => showApi.sheet(store.tenantId, id), [store.tenantId, id]);
  const [tab, setTab] = useState<Tab>('agenda');
  // While one of the reader's own debriefs is being turned into text, the sheet is read again every eight
  // seconds (the re-read keeps the page on screen), and stops once none is.
  const pending = loaded.kind === 'ready' && loaded.data.debriefs.some((d) => d.isReaders && (d.state === 'waiting' || d.state === 'transcribing'));
  useEffect(() => {
    if (!pending) return;
    const t = window.setTimeout(reload, 8000);
    return () => window.clearTimeout(t);
  }, [pending, loaded, reload]);

  if (loaded.kind === 'loading') return <div className="sh"><Loading what="the show" /></div>;
  if (loaded.kind === 'error') {
    // useLoad keeps only the message; ShowError's message leads with the status the API answered.
    const gone = loaded.message.startsWith('404');
    return (
      <div className="sh">
        <ActionButton reading type="button" className="sh-crumb" onClick={() => open('/deals')}>Deals</ActionButton>
        {gone
          ? <p className="gist">There is no show here that you can read.</p>
          : <div className="sh-says"><p className="sh-err" role="alert">The show did not load. Nothing was changed.</p><ActionButton reading type="button" className="sh-opt" onClick={reload}>Try again</ActionButton></div>}
      </div>
    );
  }
  const s = loaded.data;
  const onTabKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const at = TABS.findIndex((t) => t.key === tab);
    const to = e.key === 'ArrowRight' ? (at + 1) % TABS.length : e.key === 'ArrowLeft' ? (at + TABS.length - 1) % TABS.length
      : e.key === 'Home' ? 0 : e.key === 'End' ? TABS.length - 1 : -1;
    if (to < 0) return;
    e.preventDefault();
    setTab(TABS[to]!.key);
    document.getElementById(`sh-tab-${TABS[to]!.key}`)?.focus();
  };
  const failed = readFailedWords(s.show, (iso) => dayMonthTime(new Date(iso)));

  return (
    <div className="sh">
      <ActionButton reading type="button" className="sh-crumb" onClick={() => open('/deals')}>Deals</ActionButton>
      <div className="sh-head">
        <h1 className="sh-title">{s.show.name}</h1>
        <div className="sh-meta">
          {s.show.city ? <span>{s.show.city}</span> : null}
          {s.show.venue ? <span>{s.show.venue}</span> : null}
          {s.show.stand ? <span>Stand {s.show.stand}</span> : null}
          <span>{showDates(s.show.startsOn, s.show.endsOn)}</span>
          <span>From the team’s leads tracker</span>
        </div>
      </div>
      {failed ? <p className="sh-err" role="alert">{failed}</p> : null}

      <div className="sh-tabs" role="tablist" aria-label="The show" onKeyDown={onTabKey}>
        {TABS.map((t) => (
          // The tab pattern: the current tab is the one stop, and the arrows move between them.
          <ActionButton reading key={t.key} type="button" role="tab" id={`sh-tab-${t.key}`} aria-selected={tab === t.key}
            aria-controls={tab === t.key ? `sh-panel-${t.key}` : undefined} tabIndex={tab === t.key ? 0 : -1}
            className={tab === t.key ? 'sh-on' : undefined} onClick={() => setTab(t.key)}>
            {t.label}{t.key === 'targets' && s.notBooked.length > 0 ? <span className="sh-n">{s.notBooked.length}</span> : null}
          </ActionButton>
        ))}
      </div>

      <div role="tabpanel" id={`sh-panel-${tab}`} aria-labelledby={`sh-tab-${tab}`}>
        {tab === 'agenda' ? <Agenda s={s} me={store.principalId} open={open} store={store.tenantId} reload={reload} /> : null}
        {tab === 'targets' ? <Targets s={s} open={open} /> : null}
        {tab === 'after' ? <After s={s} open={open} store={store.tenantId} reload={reload} /> : null}
      </div>
    </div>
  );
}

function NextLine({ s, me }: { s: ShowSheet; me: string }) {
  const next = s.meetings.find((m) => m.id === s.readersNextId) ?? null;
  if (!next || !next.startsAt) {
    // "You have no meetings" when the reader has none at all; a reader whose meetings are all behind
    // them is told that instead, because "no meetings" would be untrue by the second afternoon.
    return s.readersCount === 0
      ? <p className="gist sh-next">You have no meetings at {s.show.name}. The team has {s.teamMeetingCount}.</p>
      : <p className="gist sh-next">Your meetings at {s.show.name} are behind you.</p>;
  }
  const with_ = next.contactName ? `, with ${next.contactName}` : '';
  // The colleague beside the reader on it, by the name the store holds (or as the tracker writes it).
  const other = next.primaryPrincipalId === me
    ? (next.secondName ?? next.secondRepText)
    : next.secondPrincipalId === me ? (next.primaryName ?? next.primaryRepText) : null;
  const alongside = other && other.trim() ? `, with ${other.trim()}` : '';
  return (
    <p className="gist sh-next">
      <span className="sh-plate">{plateOf(next.startsAt, s.show.timeZone)}</span>
      <span><b>Your next meeting:</b> {companyOf(next)}{with_}{alongside}.</span>
    </p>
  );
}

function Agenda({ s, me, open, store, reload }: { s: ShowSheet; me: string; open: (p: string) => void; store: string; reload: () => void }) {
  const [filter, setFilter] = useState<Filter>('everyone');
  const byId = useMemo(() => new Map(s.meetings.map((m) => [m.id, m])), [s.meetings]);
  const clashCount = s.meetings.filter((m) => m.clashes.length > 0).length;

  if (s.meetings.length === 0) return <p className="gist sh-empty">No meetings are booked for {s.show.name} yet.</p>;

  const shown = s.meetings.filter((m) => (filter === 'mine' ? m.isReaders : filter === 'clashes' ? m.clashes.length > 0 : true));
  const days = byDay(shown);

  return (
    <>
      <NextLine s={s} me={me} />
      <div className="sh-bar">
        <div className="sh-filters" role="group" aria-label="Whose meetings">
          <ActionButton reading type="button" aria-pressed={filter === 'everyone'} onClick={() => setFilter('everyone')}>Everyone <span className="sh-n">{s.teamMeetingCount}</span></ActionButton>
          <ActionButton reading type="button" aria-pressed={filter === 'mine'} onClick={() => setFilter('mine')}>Mine <span className="sh-n">{s.readersCount}</span></ActionButton>
          <ActionButton reading type="button" aria-pressed={filter === 'clashes'} onClick={() => setFilter('clashes')}>Clashes only <span className="sh-n">{clashCount}</span></ActionButton>
        </div>
        <span className="sh-zone">Times are {cityOfZone(s.show.timeZone)} time</span>
      </div>

      {shown.length === 0
        ? <p className="gist sh-empty">{filter === 'mine'
          ? `You have no meetings at ${s.show.name}. The team has ${s.teamMeetingCount}.`
          : 'Nobody of ours is booked into two meetings at once.'}</p>
        : days.map((d) => (
          <section key={d.day ?? 'none'} className="sh-day" aria-label={d.day ? dayHeading(d.day) : 'Day not stated'}>
            <h2 className="sh-sec">{d.day ? dayHeading(d.day) : 'Day not stated'}</h2>
            <div className="sh-sheet" role="table">
              <div className="sh-row sh-h" role="row">
                <span role="columnheader">Time</span><span role="columnheader">Who we meet</span><span role="columnheader">Ours</span>
                <span role="columnheader">Interested in</span><span role="columnheader">The deal it touches</span>
              </div>
              {d.meetings.map((m) => <AgendaRow key={m.id} m={m} s={s} me={me} byId={byId} open={open} store={store} reload={reload} />)}
            </div>
          </section>
        ))}
    </>
  );
}

/** A debrief can be recorded on a meeting the reader is one of ours on, once it has started, while debriefs are on. */
const mayDebrief = (m: ShowMeeting, s: ShowSheet): boolean =>
  s.debriefOn && m.kind === 'meeting' && m.isReaders && !!m.startsAt && Date.parse(m.startsAt) <= Date.parse(s.now);

function AgendaRow({ m, s, me, byId, open, store, reload }: { m: ShowMeeting; s: ShowSheet; me: string; byId: Map<string, ShowMeeting>; open: (p: string) => void; store: string; reload: () => void }) {
  const time = m.startsAt ? clockOf(m.startsAt, s.show.timeZone) : (m.timeText ?? 'Time not stated');
  const ours = oursOf(m);
  if (m.kind === 'presentation') {
    return (
      <div className="sh-row sh-pres" role="row">
        <span className="sh-t" role="cell">{time}</span>
        <span role="cell"><b>{m.companyLabel ?? 'Our presentation'}</b></span>
        <span role="cell">{ours.length ? ours.join(', ') : null}</span>
        <span role="cell" /><span role="cell" />
      </div>
    );
  }
  const who = [m.contactName, m.contactTitle].filter(Boolean).join(', ');
  return (
    <div className={`sh-row${m.isReaders ? ' sh-mine' : ''}${m.clashes.length ? ' sh-clashing' : ''}`} role="row">
      <span className="sh-t" role="cell">{time}</span>
      <span role="cell">
        {m.organisationId
          ? <ActionButton reading type="button" className="sh-co" onClick={() => open(`/companies/${m.organisationId}`)}>{companyOf(m)}</ActionButton>
          : <b>{companyOf(m)}</b>}
        {who ? <small>{who}</small> : null}
      </span>
      <span role="cell" className="sh-ours">
        <span>{ours.length ? <Ours m={m} me={me} /> : 'Not assigned'}</span>
        {m.clashes.map((c) => <span key={c.otherMeetingId + c.who} className="sh-clash">{clashWords(c, byId, s.show.timeZone)}</span>)}
        {mayDebrief(m, s) ? <DebriefRecorder store={store} s={s} meetingId={m.id} onSent={reload} /> : null}
        {mayDebrief(m, s) ? <AddNotes store={store} meetingId={m.id} /> : null}
      </span>
      <span role="cell" className="sh-c4">{interestedOf(m.products)}</span>
      <span role="cell" className="sh-c5">
        {m.deal
          ? <ActionButton reading type="button" className="sh-deal" onClick={() => open(`/deals/${m.deal!.id}`)}>{m.deal.title}</ActionButton>
          : <span className="sh-none">No deal yet</span>}
      </span>
    </div>
  );
}

/** Ours, each name on its own, with "You" after the reader's own name and nobody else's. */
function Ours({ m, me }: { m: ShowMeeting; me: string }) {
  const seats = [
    { name: m.primaryName ?? m.primaryRepText, id: m.primaryPrincipalId },
    { name: m.secondName ?? m.secondRepText, id: m.secondPrincipalId },
  ].filter((x): x is { name: string; id: string | null } => !!x.name && x.name.trim() !== '');
  const seen = new Set<string>();
  const unique = seats.filter((x) => (seen.has(x.name.trim()) ? false : (seen.add(x.name.trim()), true)));
  return <>{unique.map((x, i) => (
    <span key={i}>{i > 0 ? ', ' : ''}{x.name.trim()}{x.id !== null && x.id === me ? <span className="sh-you">You</span> : null}</span>
  ))}</>;
}

function Targets({ s, open }: { s: ShowSheet; open: (p: string) => void }) {
  const name = (t: ShowTarget) => t.organisationName ?? t.companyLabel;
  const co = (t: ShowTarget) => (t.organisationId
    ? <ActionButton reading type="button" className="sh-co" onClick={() => open(`/companies/${t.organisationId}`)}>{name(t)}</ActionButton>
    : <b>{name(t)}</b>);
  return (
    <>
      {s.notBooked.length === 0
        ? <p className="gist sh-empty">Everyone on the list has a meeting.</p>
        : (
          <div className="sh-sheet sh-t2" role="table">
            <div className="sh-row sh-h" role="row"><span role="columnheader">Company</span><span role="columnheader">The tracker’s note</span></div>
            {s.notBooked.map((t) => (
              <div key={t.id} className="sh-row" role="row">
                <span role="cell">{co(t)}</span>
                <span role="cell">{t.statusText ?? ''}</span>
              </div>
            ))}
          </div>
        )}
      {s.notAttending.length > 0
        ? (
          <>
            <h2 className="sh-sec">Not coming to the show</h2>
            <div className="sh-sheet sh-t2" role="table">
              <div className="sh-row sh-h" role="row"><span role="columnheader">Company</span><span role="columnheader">Contact</span></div>
              {s.notAttending.map((t) => (
                <div key={t.id} className="sh-row" role="row">
                  <span role="cell">{co(t)}</span>
                  <span role="cell">{[t.contactName, t.contactTitle].filter(Boolean).join(', ')}</span>
                </div>
              ))}
            </div>
          </>
        )
        : null}
    </>
  );
}

function After({ s, open, store, reload }: { s: ShowSheet; open: (p: string) => void; store: string; reload: () => void }) {
  const now = Date.parse(s.now);
  const held = s.meetings.filter((m) => m.kind === 'meeting' && m.endsAt && Date.parse(m.endsAt) <= now);
  if (held.length === 0) return <p className="gist sh-empty">Nothing held yet.</p>;
  return (
    <div className="sh-sheet sh-t3" role="table">
      <div className="sh-row sh-h" role="row"><span role="columnheader">Met</span><span role="columnheader">Ours</span><span role="columnheader">Follow-up</span></div>
      {held.map((m) => {
        const f = followUpWords(m.followUp, s.show.timeZone);
        const mine = s.debriefs.filter((d) => d.meetingId === m.id);
        return (
          <div key={m.id} className={`sh-row${m.isReaders ? ' sh-mine' : ''}`} role="row">
            <span role="cell">
              {m.organisationId
                ? <ActionButton reading type="button" className="sh-co" onClick={() => open(`/companies/${m.organisationId}`)}>{companyOf(m)}</ActionButton>
                : <b>{companyOf(m)}</b>}
              <small>{plateOf(m.startsAt!, s.show.timeZone)}</small>
            </span>
            <span role="cell">{oursOf(m).join(', ') || 'Not assigned'}</span>
            <span role="cell">{f ? <span className={f.tone === 'ok' ? 'sh-sent' : 'sh-late'}>{f.text}</span> : null}</span>
            {mine.length || mayDebrief(m, s)
              ? (
                <span role="cell" className="sh-more">
                  <DebriefList items={mine} timeZone={s.show.timeZone} />
                  {mayDebrief(m, s) ? <DebriefRecorder store={store} s={s} meetingId={m.id} onSent={reload} /> : null}
                  {mayDebrief(m, s) ? <AddNotes store={store} meetingId={m.id} /> : null}
                </span>
              )
              : null}
          </div>
        );
      })}
    </div>
  );
}
